import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import {
  optimizeImageUrl,
  optimizeImageUrlFillRectangle,
} from "@faire/web--source/common/images/optimizeImages";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import * as React from "react";

import {
  BANNER_HEIGHT_DESKTOP,
  BANNER_HEIGHT_MOBILE,
  BANNER_HEIGHT_TABLET,
} from "@faire/retailer/views/brand/BrandHeader/bannerHeightConsts";
import { INNER_MAKER_CONTAINER_MAX_WIDTH } from "@faire/retailer/views/brand/InnerMakerContainer";

type Transformation = {
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
  tabletWidth: number;
  tabletHeight: number;
  desktopWidth: number;
  desktopHeight: number;
  isManuallyCropped: boolean;
};

const MAX_EXPECTED_MOBILE_WIDTH = 480;
const transformation = {
  width: INNER_MAKER_CONTAINER_MAX_WIDTH,
  height: BANNER_HEIGHT_DESKTOP,
  mobileWidth: MAX_EXPECTED_MOBILE_WIDTH,
  mobileHeight: BANNER_HEIGHT_MOBILE,
  tabletWidth: MIN_WIDTH_BREAKPOINTS.DESKTOP,
  tabletHeight: BANNER_HEIGHT_TABLET,
  desktopWidth: INNER_MAKER_CONTAINER_MAX_WIDTH,
  desktopHeight: BANNER_HEIGHT_DESKTOP,
};

const loadImage = (imageUrl: string, onLoad: () => void) => {
  try {
    const window = getWindowOrThrow();
    const img = new window.Image();
    img.onload = onLoad;
    img.src = imageUrl;
    img.onerror = onLoad;
  } catch (_) {
    onLoad();
  }
};

const convertToHigherRes = (transformation: Transformation): Transformation => {
  return {
    ...transformation,
    width: transformation.width * 2,
    height: transformation.height * 2,
  };
};

const doTransformation = (
  image: IImage,
  isSSR: boolean,
  transformation: Transformation
) => {
  if (transformation.isManuallyCropped) {
    return optimizeImageUrl(image, isSSR, {
      height: transformation.height,
    });
  }
  return optimizeImageUrlFillRectangle(
    image,
    isSSR,
    transformation.width,
    transformation.height
  );
};

interface IProgressiveBrandCoverImageProps {
  image: IImage;
  isSSR: boolean;
  isManuallyCropped: boolean;
  alt: string;
  onLoad?: () => void;
}

export const ProgressiveBrandCoverImage: React.FC<
  IProgressiveBrandCoverImageProps
> = ({ image, isManuallyCropped, isSSR, alt, onLoad }) => {
  const { isMobile, isTablet, isDesktop } = useViewport();
  const transform = React.useMemo(() => {
    if (isManuallyCropped) {
      return {
        ...transformation,
        height: 240,
        isManuallyCropped,
      };
    } else if (isMobile) {
      return {
        ...transformation,
        width: transformation.mobileWidth,
        height: transformation.mobileHeight,
        isManuallyCropped,
      };
    } else if (isTablet) {
      return {
        ...transformation,
        width: transformation.tabletWidth,
        height: transformation.tabletHeight,
        isManuallyCropped,
      };
    } else if (isDesktop) {
      return {
        ...transformation,
        width: transformation.desktopWidth,
        height: transformation.desktopHeight,
        isManuallyCropped,
      };
    }

    return { ...transformation, isManuallyCropped };
  }, [isManuallyCropped, isMobile, isTablet, isDesktop]);

  const [imageUrl, setImageUrl] = React.useState(() =>
    doTransformation(image, isSSR, transform)
  );

  React.useEffect(() => {
    const fullSizeUrl = doTransformation(
      image,
      isSSR,
      convertToHigherRes(transform)
    );
    if (fullSizeUrl) {
      loadImage(fullSizeUrl, () => setImageUrl(fullSizeUrl));
    }
  }, [image, isSSR, transform]);

  return (
    <img
      data-faire-image-id={image.token}
      src={imageUrl}
      alt={alt}
      onLoad={onLoad}
    />
  );
};
